import { Button, LoadingSpinner, Modal, SearchBar } from "@ai-teach/ui-web";
import { useState } from "react";
import { FaRegNewspaper } from "react-icons/fa";
import { HiFolder } from "react-icons/hi";
import { MdChevronRight, MdDriveFileMove } from "react-icons/md";

import { useSelection } from "@/contexts/SelectionContext";
import { useLessonNavigation } from "@/lib/hooks/modal/useLessonNavigation";
import { useMoveSlides } from "@/lib/hooks/slides/useMoveSlides";

interface MoveSlideModalProps {
  isOpen: boolean;
  sourceLessonId: string;
  defaultProjectStack?: Array<{ id: string; name: string }> | undefined;
}

export function MoveSlideModal({
  isOpen,
  sourceLessonId,
  defaultProjectStack,
}: MoveSlideModalProps) {
  const [searchText, setSearchText] = useState("");

  const { selectedSlideIds, clearAllSelections, toggleMoveSlideModal } = useSelection();

  const {
    currentLessonId,
    projects,
    lessonsWithoutLimitation,
    isEmpty,
    currentParent,
    navigationStack,
    hasMoreLesson,
    hasMoreProjects,
    lessonLoading,
    projectLoading,
    handleProjectClick,
    handleLessonClick,
    navigateToIndex,
    resetNavigation,
    loadMoreProjects,
    loadMoreLesson,
  } = useLessonNavigation(searchText, { initialProjectStack: defaultProjectStack });

  const lessonsProjectsLoading = lessonLoading || projectLoading;

  const { moveSlides, loading: moveSlidesLoading } = useMoveSlides();

  const selectedLessonName = lessonsWithoutLimitation.find((l) => l.id === currentLessonId)?.name;

  const handleMove = async () => {
    if (!currentLessonId) return;
    await moveSlides(selectedSlideIds, currentLessonId, sourceLessonId);
    handleClose();
  };

  const handleClose = () => {
    resetNavigation();
    setSearchText("");
    toggleMoveSlideModal(false);
    clearAllSelections();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Move ${selectedSlideIds.length} item${selectedSlideIds.length !== 1 ? "s" : ""}`}
    >
      <div className="space-y-4">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search projects, lessons..."
        />

        {/* Breadcrumb navigation */}
        <nav
          aria-label="Folder breadcrumb"
          className="flex items-center gap-1 text-sm min-w-0 overflow-hidden flex-wrap"
        >
          <button
            type="button"
            onClick={() => navigateToIndex(-1)}
            className={`flex-shrink-0 font-medium transition-colors ${
              navigationStack.length === 0
                ? "text-secondary-200 cursor-default"
                : "text-gray-400 hover:text-primary-500"
            }`}
          >
            Home
          </button>
          {navigationStack.map((item, i) => {
            const isLast = i === navigationStack.length - 1;
            return (
              <span key={item.id} className="flex items-center gap-1 min-w-0">
                <MdChevronRight className="w-4 h-4 flex-shrink-0 text-gray-600" />
                <button
                  type="button"
                  onClick={() => navigateToIndex(i)}
                  className={`truncate font-medium transition-colors ${
                    isLast
                      ? "text-secondary-200 cursor-default"
                      : "text-gray-400 hover:text-primary-500"
                  }`}
                >
                  {item.name}
                </button>
              </span>
            );
          })}
        </nav>

        {/* Destination summary banner */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-800">
          <MdDriveFileMove className="w-4 h-4 flex-shrink-0 text-primary-500" />
          {currentLessonId && selectedLessonName ? (
            <p className="text-sm text-gray-400">
              Moving to:{" "}
              <span className="font-semibold text-secondary-200">{selectedLessonName}</span>
            </p>
          ) : (
            <p className="text-sm text-gray-500">Select a lesson below</p>
          )}
        </div>

        <div className="h-96 overflow-y-auto border border-gray-800 rounded-lg gradient-scrollbar">
          {lessonsProjectsLoading &&
          projects.length === 0 &&
          lessonsWithoutLimitation.length === 0 ? (
            <div className="p-4 flex gap-2 flex-col items-center justify-center h-full caption">
              <LoadingSpinner />
              <p className="animate-pulse">Loading...</p>
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center gap-2 h-full py-8 text-center">
              <MdDriveFileMove className="w-8 h-8 text-primary-500" />
              <p className="text-sm text-gray-400">
                {currentParent
                  ? "No projects or lessons in this folder"
                  : "No projects or lessons found"}
              </p>
              <p className="text-xs text-gray-600">Try searching or navigating to another folder</p>
            </div>
          ) : (
            <>
              {/* Projects Section */}
              {projects.length > 0 && (
                <>
                  <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-100 bg-primary border-b border-gray-800">
                    Projects
                  </div>
                  {projects.map((project) => {
                    const isDisabled = project.isLinkedCopy || project.hasLinkedCopy;
                    return (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() =>
                          !isDisabled && handleProjectClick({ id: project.id, name: project.name })
                        }
                        disabled={isDisabled}
                        className={`w-full flex items-center justify-between p-3 border-b border-gray-800 last:border-b-0 transition-colors
                          ${
                            isDisabled
                              ? "bg-gray-800/20 cursor-not-allowed opacity-60"
                              : "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                          }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <HiFolder
                            className={`w-5 h-5 flex-shrink-0 ${isDisabled ? "text-gray-500" : "text-primary-500"}`}
                          />
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`font-medium truncate ${isDisabled ? "text-gray-500" : "text-secondary-200"}`}
                            >
                              {project.name}
                            </span>
                            {project.isLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                Linked Copy
                              </span>
                            )}
                            {project.hasLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-red-400 rounded-full">
                                Has linked copy
                              </span>
                            )}
                          </div>
                        </div>
                        {!isDisabled && (
                          <MdChevronRight className="w-5 h-5 text-secondary-200 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}

                  {hasMoreProjects && !projectLoading && (
                    <button
                      type="button"
                      onClick={loadMoreProjects}
                      className="w-full p-3 text-center text-sm text-primary-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      Load more projects...
                    </button>
                  )}

                  {projectLoading && (
                    <div className="flex justify-center items-center py-3 gap-2 text-muted">
                      <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span>Loading more projects...</span>
                    </div>
                  )}
                </>
              )}

              {/* Lessons Section */}
              {lessonsWithoutLimitation.length > 0 && (
                <>
                  <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-100 bg-primary border-b border-gray-800">
                    Lessons
                  </div>
                  {lessonsWithoutLimitation.map((lesson) => {
                    const isDisabled =
                      lesson.isLinkedCopy || lesson.hasLinkedCopy || lesson.id === sourceLessonId;
                    const isSelected = currentLessonId === lesson.id;
                    return (
                      <button
                        key={lesson.id}
                        type="button"
                        onClick={() =>
                          !isDisabled && handleLessonClick({ id: lesson.id, name: lesson.name })
                        }
                        disabled={isDisabled}
                        className={`w-full flex items-center justify-between p-3 border-b border-gray-800 last:border-b-0 transition-colors
                          ${isSelected ? "bg-primary-500/10 ring-2 ring-primary-500" : ""}
                          ${
                            isDisabled
                              ? "bg-gray-800/20 cursor-not-allowed opacity-60"
                              : "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                          }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FaRegNewspaper
                            className={`w-5 h-5 flex-shrink-0 ${isDisabled ? "text-gray-500" : "text-green-500"}`}
                          />
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`font-medium truncate ${isDisabled ? "text-gray-500" : "text-secondary-200"}`}
                            >
                              {lesson.name}
                            </span>
                            {lesson.isLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                Linked Copy
                              </span>
                            )}
                            {lesson.hasLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-red-400 rounded-full">
                                Has linked copy
                              </span>
                            )}
                            {lesson.id === sourceLessonId && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-gray-500/20 text-gray-400 rounded-full">
                                Current Lesson
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <span className="text-xs font-semibold text-primary-500 flex-shrink-0">
                            Selected
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {hasMoreLesson && (
                    <button
                      type="button"
                      onClick={loadMoreLesson}
                      disabled={lessonLoading}
                      className="w-full p-3 flex items-center justify-center gap-2 text-sm text-primary-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {lessonLoading ? (
                        <>
                          <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load more lessons..."
                      )}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleMove}
            disabled={!currentLessonId || moveSlidesLoading}
          >
            {moveSlidesLoading ? "Moving..." : "Move"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
